import { Link } from 'react-router-dom';
import { HelpLink } from '../help/HelpLink';
import { OPEN_STATUSES, de, noticeLevelLabel, statusLabel } from '@fsv/core';
import { MiniDrawing } from '../components/MiniDrawing';
import { StatusGlyph } from '../components/Status';
import { useAircraftTypes, useFleet, useNotices, useTenantPath } from '../data/hooks';
import { Icon } from '../components/Icon';
import '../components/NoticePanel.css';
import type { FleetEntry } from '@fsv/data';
import { resolveDrawing } from '../drawings/useDrawing';
import { tx } from '@fsv/data';
import { formatRelative, noticeMark } from '@fsv/data';
import { ErrorState, Loading } from './states';
import './FleetScreen.css';

function FleetCard({ entry }: { entry: FleetEntry }) {
  const types = useAircraftTypes();
  const type = types.data?.find((t) => t.id === entry.aircraft.typeId);
  const top = entry.openPins.filter((p) => p.view === 'oben');
  const elsewhere = entry.openPins.length - top.length;
  const { aircraft } = entry;
  const to = useTenantPath();
  const mark = noticeMark(useNotices().data, aircraft.id);
  return (
    <li>
      <Link to={to(`/flugzeug/${aircraft.registration}/plan`)} className="fcard" data-tour="fleet-card">
        <div className="fcard__head">
          <span className="fcard__reg mono">{aircraft.registration}</span>
          <span className="fcard__type label">{aircraft.typeName}</span>
        </div>
        {mark && (
          <span className={`fcard__notices fcard__notices--${mark.highest}`} data-testid="fleet-notice-mark" title={de.notices.title}>
            <Icon name={mark.highest === 'gesperrt' ? 'lock' : 'alert'} />
            {mark.highest === 'gesperrt' ? `${noticeLevelLabel.gesperrt} · ${de.notices.count(mark.count)}` : de.notices.count(mark.count)}
          </span>
        )}
        <MiniDrawing className="fcard__drawing" resolved={type ? resolveDrawing(type, 'oben') : null} pins={top.map((p) => ({ id: p.id, x: p.x, y: p.y, status: p.status }))} pinScale={1.6} />
        <div className="fcard__block">
          <div className="fcard__cell fcard__cell--wide">
            <span className="label">{entry.openCount ? tx.fleet.open(entry.openCount) : tx.fleet.noneOpen}</span>
            <span className="fcard__counts">
              {OPEN_STATUSES.filter((s) => entry.counts[s] > 0).map((s) => (
                <span key={s} className={`fcard__count st--${s}`} title={statusLabel[s]}>
                  <StatusGlyph status={s} size={12} />
                  <span className="mono">{entry.counts[s]}</span>
                  <span className="sr-only">{statusLabel[s]}</span>
                </span>
              ))}
              {elsewhere > 0 && <span className="fcard__else">{tx.fleet.otherViews(elsewhere)}</span>}
            </span>
          </div>
          <div className="fcard__cell">
            <span className="label">{tx.fleet.lastReport}</span>
            <span className="fcard__date">{entry.lastReportAt ? formatRelative(entry.lastReportAt, new Date()) : tx.fleet.never}</span>
          </div>
        </div>
        {entry.counts.gemeldet > 0 && (
          <span className="fcard__flag">
            <StatusGlyph status="gemeldet" size={10} />
            {entry.counts.gemeldet} {statusLabel.gemeldet.toLowerCase()}
          </span>
        )}
      </Link>
    </li>
  );
}

export function FleetScreen() {
  const fleet = useFleet();
  return (
    <div className="page">
      <header className="page__head">
        <HelpLink screen="fleet" className="page__help" />
        <h1>{tx.fleet.title}</h1>
        <p className="muted">{tx.fleet.subtitle}</p>
      </header>
      {fleet.isPending ? (
        <Loading />
      ) : fleet.isError ? (
        <ErrorState error={fleet.error} onRetry={() => void fleet.refetch()} />
      ) : (
        <ul className="fleet">
          {fleet.data.map((e) => (
            <FleetCard key={e.aircraft.id} entry={e} />
          ))}
        </ul>
      )}
    </div>
  );
}
